import { Component, Input, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ViewMode } from '../../../../models';
import { DiscountRejectionCouponExtension } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { isRichTextEmpty } from '../../../extension-components/discount-rejection-coupon/model';

@Component({
  selector: 'discount-rejection-coupon-example',
  templateUrl: './discount-rejection-coupon-example.component.html',
  styleUrls: ['./discount-rejection-coupon-example.component.scss'],
  standalone: false,
})
export class DiscountRejectionCouponExampleComponent {
  @Input() template: DiscountRejectionCouponExtension.Template;
  @Input() mode: ViewMode = null;
  @Input() isMobile: boolean = false;

  constructor(private sanitizer: DomSanitizer) {}

  get activeConfiguration(): DiscountRejectionCouponExtension.Configuration | null {
    const configs = this.template?.configurations;
    if (!configs?.length) return null;
    return configs.find((c: DiscountRejectionCouponExtension.Configuration) => c.active) ?? configs[0];
  }

  get messageTitle(): string {
    const raw = this.activeConfiguration?.messageTitle || '';
    if (isRichTextEmpty(raw)) return '';
    return this.replaceDynamicVariables(raw);
  }

  get messageDescription(): string {
    const raw = this.activeConfiguration?.messageDescription || '';
    if (isRichTextEmpty(raw)) return '';
    return this.replaceDynamicVariables(raw);
  }

  get widgetType(): DiscountRejectionCouponExtension.WidgetType {
    return this.template?.appearance?.widgetType || DiscountRejectionCouponExtension.WidgetType.BANNER;
  }

  get backgroundColor(): string {
    return this.template?.appearance?.widgetAppearance?.backgroundColor || 'info';
  }
  get textColor(): string {
    const option = this.template?.appearance?.widgetAppearance?.textColor || 'default';
    if (option === 'matchBanner') {
      return this.backgroundColor;
    }
    return 'default';
  }

  get buttonCtaText(): string {
    return this.template?.appearance?.buttonAppearance?.buttonCtaText || 'Claim';
  }

  get buttonAppearance(): string {
    return this.template?.appearance?.buttonAppearance?.buttonAppearance || 'monochrome';
  }

  get bannerIcon(): string {
    return this.iconMap[this.backgroundColor] || 'more_information';
  }

  WidgetType = DiscountRejectionCouponExtension.WidgetType;

  private readonly SAMPLE_COUPON_CODE = 'SAVE-X768787';
  private readonly SAMPLE_CUSTOMER_FIRST_NAME = 'John';
  private readonly SAMPLE_CUSTOMER_LAST_NAME = 'Doe';

  private readonly iconMap: Record<string, string> = {
    info: 'more_information',
    success: 'v_icon',
    warning: 'Attention',
    critical: 'error',
  };

  private get replacementDiscountPreview(): string {
    const config = this.activeConfiguration;
    if (!config) return this.SAMPLE_COUPON_CODE;
    if (config.replacementDiscountRule === DiscountRejectionCouponExtension.ReplacementRule.EXISTING_DISCOUNT) {
      return config.storeDiscount || this.SAMPLE_COUPON_CODE;
    }
    return this.SAMPLE_COUPON_CODE;
  }

  private replaceDynamicVariables(text: string): string {
    const processed = text
      .replace(/\{\{\s*coupon_code\s*\}\}/g, this.SAMPLE_COUPON_CODE)
      .replace(/\{replacement_discount\}/g, this.replacementDiscountPreview)
      .replace(/\{customer\.first_name\}/g, this.SAMPLE_CUSTOMER_FIRST_NAME)
      .replace(/\{customer\.last_name\}/g, this.SAMPLE_CUSTOMER_LAST_NAME);

    return this.sanitizer.sanitize(SecurityContext.HTML, processed) || '';
  }
}
